// ---------------------------------------------------------------------------
// Central game configuration. All tunable numbers live here so balancing the
// game never requires hunting through component files.
// ---------------------------------------------------------------------------
export const CONFIG = {
  startingMoney: 1000,
  startingDebt: 500,
  startingLocation: "Bronx",
  maxDays: 30,

  debtLimit: 5000,
  dailyInterest: 0.15, // debt grows 15% each day it goes unpaid

  policeChance: 0.2, // chance of a police encounter when traveling
  escapeChance: 0.5, // chance of getting away clean when you choose to run

  // Police choices scale with the cash you're carrying (a fat bankroll is a
  // bigger target). Fine = cheaper, but you LOSE your stash. Bribe = pricier,
  // but you KEEP your stash (borrow the shortfall if you can't cover it).
  finePercent: 0.2,
  fineMin: 50,
  bribePercent: 0.35,
  bribeMin: 150,

  // If you run and get caught, the severity is rolled from these tiers. Assets
  // are always safe — only exposed cash and drugs are at risk. Chances should
  // sum to 1.
  raidOutcomes: [
    { chance: 0.5, type: "drugs",         message: "🚔 Busted! Police seized your entire stash. Your cash and assets are safe." },
    { chance: 0.3, type: "drugsHalfCash", message: "🚔 Busted! They took your stash and half your cash. Your assets are safe." },
    { chance: 0.2, type: "all",           message: "🚔 Busted hard! They took all your cash and drugs. Only your assets remain." },
  ],

  assetAppreciation: 0.015, // daily appreciation for appreciating assets
  carDepreciation: 0.02, // daily depreciation for "lifestyle" assets (cars, etc.)

  inventoryCapacity: 100, // total units of product you can carry at once

  eventChancePerDrug: 0.18, // chance each drug gets a market event each day
};

export const initialPlayerState = {
  money: CONFIG.startingMoney,
  debt: CONFIG.startingDebt,
  location: CONFIG.startingLocation,
  inventory: {},
  assets: [],
  day: 1,
  maxDays: CONFIG.maxDays,
};

// ---------------------------------------------------------------------------
// Drugs — each has its own price band and volatility so they feel distinct.
// `volatility` widens day-to-day swings; pricier, riskier product swings more.
// ---------------------------------------------------------------------------
export const DRUGS = [
  { name: "Weed",    min: 20,  max: 180,  volatility: 0.25 },
  { name: "Meth",    min: 100, max: 800,  volatility: 0.45 },
  { name: "Cocaine", min: 400, max: 1600, volatility: 0.55 },
  { name: "Heroin",  min: 600, max: 2800, volatility: 0.70 },
];

// Backwards-compatible list of names (used anywhere we just need the keys).
export const drugs = DRUGS.map(d => d.name);

// ---------------------------------------------------------------------------
// Locations — each biases drug prices differently, which is what makes
// traveling worthwhile: buy where a drug is cheap, sell where it's pricey.
// A modifier of 1.0 is neutral; <1 is a buyer's market, >1 a seller's market.
// ---------------------------------------------------------------------------
export const LOCATIONS = [
  {
    name: "Bronx",
    blurb: "Cheap supply of hard product, but coke is scarce.",
    modifiers: { Weed: 0.9, Meth: 0.75, Cocaine: 1.3, Heroin: 0.8 },
  },
  {
    name: "Manhattan",
    blurb: "Money everywhere — premium prices, premium demand.",
    modifiers: { Weed: 1.15, Meth: 1.2, Cocaine: 1.4, Heroin: 1.35 },
  },
  {
    name: "Brooklyn",
    blurb: "Weed capital; everything else sits mid-market.",
    modifiers: { Weed: 0.7, Meth: 1.0, Cocaine: 0.95, Heroin: 1.05 },
  },
];

export const locations = LOCATIONS.map(l => l.name);

// Possible market shocks. A "spike" makes a drug expensive (great to sell,
// painful to buy); a "crash" makes it cheap (great to buy). Returned messages
// are shown to the player so the swing feels like a story beat.
const MARKET_EVENTS = [
  { type: "spike", mult: 3.0, message: (d) => `🔥 Addicts are clamoring for ${d}! Prices are sky high.` },
  { type: "spike", mult: 2.0, message: (d) => `📈 A ${d} shortage hits the streets — prices surge.` },
  { type: "crash", mult: 0.4, message: (d) => `📉 The market is flooded with ${d} — prices crash.` },
  { type: "crash", mult: 0.5, message: (d) => `🧊 A big ${d} bust dumped cheap product everywhere.` },
];

// ---------------------------------------------------------------------------
// Market price engine. Produces a price per drug for the given location, plus
// any market-event messages that fired this turn.
// ---------------------------------------------------------------------------
export const generateMarketPrices = (locationName) => {
  const location = LOCATIONS.find(l => l.name === locationName) || LOCATIONS[0];
  const prices = {};
  const events = [];

  DRUGS.forEach(drug => {
    // Base price: random point in the band, nudged by the drug's volatility.
    const span = drug.max - drug.min;
    const roll = Math.random();
    const volatilitySwing = (Math.random() - 0.5) * 2 * drug.volatility;
    let price = drug.min + span * roll * (1 + volatilitySwing);

    // Location bias.
    price *= location.modifiers[drug.name] ?? 1;

    // Occasional market shock.
    if (Math.random() < CONFIG.eventChancePerDrug) {
      const event = MARKET_EVENTS[Math.floor(Math.random() * MARKET_EVENTS.length)];
      price *= event.mult;
      events.push(event.message(drug.name));
    }

    prices[drug.name] = Math.max(1, Math.round(price));
  });

  return { prices, events };
};

export const availableAssets = [
  { name: "Exotic Car", price: 5000 },
  { name: "Mansion", price: 25000 },
  { name: "Apartment Building", price: 10000 },
  { name: "Business Venture", price: 20000 }
];

// Assets that appreciate over time (vs. ones that depreciate like luxuries).
export const APPRECIATING_ASSETS = ["Apartment Building", "Business Venture"];
export const isAppreciatingAsset = (name) => APPRECIATING_ASSETS.includes(name);

export const formatCurrency = (amount) => {
  return `$${Math.round(amount).toLocaleString("en-US")}`;
};

export const calculateAppreciatedValue = (basePrice, purchaseDay, currentDay) => {
  const daysHeld = currentDay - purchaseDay;
  return basePrice * Math.pow(1 + CONFIG.assetAppreciation, daysHeld);
};

// Lifestyle assets (cars, mansions) lose value the longer you hold them.
export const calculateDepreciatedValue = (basePrice, purchaseDay, currentDay) => {
  const daysHeld = currentDay - purchaseDay;
  return basePrice * Math.pow(1 - CONFIG.carDepreciation, daysHeld);
};

// Current resale value of one unit of an asset, given how long it's been held.
export const assetUnitValue = (asset, currentDay) => {
  if (isAppreciatingAsset(asset.name)) {
    return calculateAppreciatedValue(asset.basePrice, asset.purchaseDay, currentDay);
  }
  // Lifestyle assets depreciate; fall back to base price for legacy saves
  // that don't carry a purchaseDay.
  if (asset.purchaseDay != null) {
    return calculateDepreciatedValue(asset.basePrice ?? asset.price, asset.purchaseDay, currentDay);
  }
  return asset.basePrice || asset.price;
};

// Single source of truth for valuing a player's assets and net worth, so the
// header, end-game screen, and any future UI all agree.
export const calculateTotalAssetsValue = (player) => {
  return player.assets.reduce((total, asset) => {
    return total + assetUnitValue(asset, player.day) * asset.quantity;
  }, 0);
};

export const calculateNetWorth = (player) => {
  return player.money + calculateTotalAssetsValue(player) - player.debt;
};

// Total units of product currently carried (counts against capacity).
export const totalCarried = (inventory) => {
  return Object.values(inventory).reduce((sum, n) => sum + (n || 0), 0);
};

// A dead end: no cash, no product to sell, no assets to liquidate, and the debt
// is maxed out so you can't borrow your way back in. Game over — you lose.
export const isBankrupt = (player) => {
  const noCash = player.money <= 0;
  const noDrugs = totalCarried(player.inventory) === 0;
  const noAssets = player.assets.length === 0;
  const cantBorrow = player.debt >= CONFIG.debtLimit;
  return noCash && noDrugs && noAssets && cantBorrow;
};

// Cash penalties scale with how much money you're carrying (with a floor so
// they aren't trivial). Fine is the cheaper hit; bribe costs more.
export const calculateFine = (money) =>
  Math.max(Math.round(money * CONFIG.finePercent), CONFIG.fineMin);

export const calculateBribe = (money) =>
  Math.max(Math.round(money * CONFIG.bribePercent), CONFIG.bribeMin);

// Resolve a bribe attempt. You pay from cash first, then the loan shark fronts
// the rest — but only up to the debt limit. If even maxed-out borrowing can't
// cover it, the bribe FAILS (caller seizes the stash). "Maxed means maxed."
//   -> { type: 'paid' | 'borrowed' | 'failed', money, debt, borrowed }
export const resolveBribe = (money, debt, bribe) => {
  if (money >= bribe) {
    return { type: 'paid', money: money - bribe, debt, borrowed: 0 };
  }
  const headroom = Math.max(0, CONFIG.debtLimit - debt);
  const shortfall = bribe - money;
  if (shortfall <= headroom) {
    return { type: 'borrowed', money: 0, debt: debt + shortfall, borrowed: shortfall };
  }
  return { type: 'failed', money, debt, borrowed: 0 };
};

// Pick a raid-severity outcome by its weighted chance.
export const rollRaidOutcome = () => {
  let roll = Math.random();
  for (const outcome of CONFIG.raidOutcomes) {
    if (roll < outcome.chance) return outcome;
    roll -= outcome.chance;
  }
  return CONFIG.raidOutcomes[CONFIG.raidOutcomes.length - 1];
};
